import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

/**
 * v1.2 PR-B.3 — Theme preference (auto/light/dark).
 *
 * Source of truth for the chosen preference:
 *   1. The authenticated user's `theme_preference` field (loaded from /auth/me)
 *   2. localStorage `eb_theme_preference` for not-logged-in screens (signup/login/etc.)
 *
 * Resolved theme (what's actually applied to <html>) depends on:
 *   - "light" → html.classList = ""
 *   - "dark"  → html.classList = "dark"
 *   - "auto"  → follow prefers-color-scheme
 *
 * Cross-tab sync via BroadcastChannel('theme') so an update in tab A is
 * reflected in tab B without a reload.
 *
 * Out of scope (intentional light-only):
 *   - /kiosk/* — full-screen sign-in display, brand-locked
 *   - /visitor-pass/*
 *   - Generated PDF reports / badges / print views
 *   - Email templates
 */
const ThemeContext = createContext(null);

const STORAGE_KEY = "eb_theme_preference";
const BROADCAST_CHANNEL = "eb-theme";

function resolveTheme(pref) {
  if (pref === "light" || pref === "dark") return pref;
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return "light";
}

function applyClass(theme) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  // Never apply dark on routes that are intentionally light-only.
  const path = window.location.pathname || "";
  if (path.startsWith("/kiosk") || path.startsWith("/visitor-pass")) {
    root.classList.remove("dark");
    return;
  }
  if (theme === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
}

export function ThemeProvider({ children }) {
  const [preference, setPreferenceState] = useState(() => {
    if (typeof localStorage === "undefined") return "auto";
    return localStorage.getItem(STORAGE_KEY) || "auto";
  });
  const [resolved, setResolved] = useState(() => resolveTheme(preference));

  // Apply on mount + every change
  useEffect(() => {
    const next = resolveTheme(preference);
    setResolved(next);
    applyClass(next);
  }, [preference]);

  // Listen for system preference changes when in "auto"
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      if (preference === "auto") {
        const next = mq.matches ? "dark" : "light";
        setResolved(next);
        applyClass(next);
      }
    };
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, [preference]);

  // Cross-tab sync
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const bc = new BroadcastChannel(BROADCAST_CHANNEL);
    bc.onmessage = (e) => {
      if (e?.data?.preference && e.data.preference !== preference) {
        setPreferenceState(e.data.preference);
      }
    };
    return () => bc.close();
  }, [preference]);

  const setPreference = useCallback((next) => {
    setPreferenceState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {}
    // Broadcast to other tabs.
    try {
      if (typeof BroadcastChannel !== "undefined") {
        const bc = new BroadcastChannel(BROADCAST_CHANNEL);
        bc.postMessage({ preference: next });
        bc.close();
      }
    } catch {}
  }, []);

  return (
    <ThemeContext.Provider value={{ preference, resolved, setPreference }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
